import { useNavigate } from 'react-router';
import { DateRangeState } from '../../DateRangeControl/useDateRange';
import { MULTIPLE_SALON_CHART_KPI } from './constants';
import { severity, useNotification } from '../../../Notification';
import { useUserStore } from '../../../../store/UserStore';
import { useEffect, useState } from 'react';
import { fetchData } from '../../../../axios/fetchData';
import { SalonsByKpi, SalonsByKpiData } from './types';
import { isDevMode } from '../../../../utils/isDevMode';
import { getDateRange } from '../../DateRangeControl/helpers';

type DashboardSalonsByKpiType = {
  dateRange: DateRangeState;
  salonClassification: string;
  multipleSalonsChartKpi: keyof typeof MULTIPLE_SALON_CHART_KPI;
  offset: number;
  limit: number;
};

export const useSalonsByKpiData = ({
  dateRange,
  salonClassification,
  multipleSalonsChartKpi,
  offset,
  limit,
}: DashboardSalonsByKpiType) => {
  const { showMessage } = useNotification();
  const { user } = useUserStore();
  const navigate = useNavigate();

  const [salonsByKpiData, setSalonsByKpiData] = useState<SalonsByKpiData>({});

  useEffect(() => {
    async function load() {
      const { dateString } = getDateRange({
        period: dateRange.name,
        year: dateRange.year,
        quarter: dateRange.quarter,
      });

      const endpoint = MULTIPLE_SALON_CHART_KPI[multipleSalonsChartKpi];

      const url = isDevMode()
        ? `${import.meta.env.VITE_APP_SDP_DASHBOARD}chart-data/${endpoint}_${limit}_${offset}.json`
        : `${import.meta.env.VITE_APP_SDP_DASHBOARD}chart_data/${user.id}/${salonClassification}/${endpoint}/cd/${dateString}?limit=${limit}&offset=${offset}`;

      try {
        const result = await fetchData(
          {
            url,
            params: {
              _format: 'json',
            },
          },
          (message: string, type: severity) => {
            if (message !== 'A Dashboard data not found.') showMessage(message, type);
          },
          navigate,
        );

        if (result) {
          setSalonsByKpiData((prev) => ({
            ...prev,
            [multipleSalonsChartKpi]: result.data as SalonsByKpi,
          }));
        }
      } catch (err) {
        console.error(`Error loading ${url}`, err);
      }
    }

    setSalonsByKpiData({});

    if (user?.id && dateRange && salonClassification && multipleSalonsChartKpi) {
      load();
    }
  }, [dateRange, salonClassification, multipleSalonsChartKpi, offset, limit]);

  return {
    salonsByKpiData,
  };
};
